import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(undefined);

// Complete translations for all supported languages
const translations = {
  nl: {
    // Navigation & General
    dashboard: 'Dashboard',
    customers: 'Klanten',
    properties: 'Adressen',
    cleaning: 'Reinigingen',
    installations: 'Installaties',
    quotes: 'Offertes',
    invoices: 'Facturen',
    agenda: 'Agenda',
    stock: 'Voorraad',
    settings: 'Instellingen',
    logout: 'Uitloggen',
    collapse: 'Inklappen',
    expand: 'Uitklappen',
    platformAdmin: 'Platform Beheer',
    products: 'Producten',
    requests: 'Aanvragen',
    salesOverview: 'Sales',
    admin: 'Admin',
    map: 'Kaart',
    mails: 'Mails',
    tickets: 'Tickets',
    
    // Header
    welcome: 'Welkom',
    welcomeBack: 'Welkom terug!',
    
    // Dashboard
    overviewOfInstallations: 'Overzicht van je zonne-installaties',
    totalCustomers: 'Totaal Klanten',
    totalAddresses: 'Totaal Adressen',
    totalSolarPanels: 'Totaal Zonnepanelen',
    contract2Year: '2-Jaarlijks Contract',
    contract3Year: '3-Jaarlijks Contract',
    cleaningNeeded: 'Reiniging Nodig',
    installationsOverview: 'Installaties',
    installationsPerCity: 'Installaties per Stad',
    distributionOverLocations: 'Verdeling over locaties',
    
    // Actions
    save: 'Opslaan',
    cancel: 'Annuleren',
    delete: 'Verwijderen',
    edit: 'Bewerken',
    add: 'Toevoegen',
    search: 'Zoeken',
    filter: 'Filter',
    export: 'Exporteren',
    import: 'Importeren',
    create: 'Aanmaken',
    close: 'Sluiten',
    confirm: 'Bevestigen',
    back: 'Terug',
    next: 'Volgende',
    previous: 'Vorige',
    view: 'Bekijken',
    download: 'Downloaden',
    upload: 'Uploaden',
    refresh: 'Vernieuwen',
    
    // Settings
    settingsTitle: 'Instellingen',
    settingsDescription: 'Beheer je account, backups en voorkeuren',
    profile: 'Profiel',
    yourAccountDetails: 'Jouw accountgegevens',
    appearance: 'Weergave',
    chooseTheme: 'Kies je voorkeursthema',
    dark: 'Donker',
    light: 'Licht',
    darkMode: 'Dark mode',
    lightMode: 'Light mode',
    language: 'Taal',
    chooseLanguage: 'Kies je voorkeurstaal',
    dutch: 'Nederlands',
    english: 'English',
    polish: 'Polski',
    ukrainian: 'Українська',
    changePassword: 'Wachtwoord Wijzigen',
    currentPassword: 'Huidig Wachtwoord',
    newPassword: 'Nieuw Wachtwoord',
    confirmPassword: 'Bevestig Wachtwoord',
    updatePassword: 'Wachtwoord Bijwerken',
    roleAndPermissions: 'Rol & Rechten',
    yourAccessLevel: 'Jouw toegangsniveau in het systeem',
    systemInfo: 'Systeeminformatie',
    platformDetails: 'Platformdetails',
    platform: 'Platform',
    version: 'Versie',
    autoBackup: 'Auto-backup',
    every10Minutes: 'Elke 10 minuten',
    companyDetails: 'Bedrijfsgegevens',
    companyDetailsDesc: 'Deze gegevens worden gebruikt op offertes en documenten',
    companyLogo: 'Bedrijfslogo',
    companyName: 'Bedrijfsnaam',
    address: 'Adres',
    vatNumber: 'BTW Nummer',
    email: 'E-mail',
    phone: 'Telefoon',
    
    // Roles
    administrator: 'Administrator',
    technician: 'Technicus',
    sales: 'Sales',
    cleaner: 'Kuisman',
    adminDesc: 'Als Administrator heb je volledige toegang tot alle functies, inclusief gebruikersbeheer, data verwijdering, backups en systeeminstellingen.',
    technicianDesc: 'Als Technicus kun je installaties beheren, onderhoudsrecords toevoegen en reinigingsactiviteiten plannen.',
    salesDesc: 'Als Sales kun je klantgegevens bekijken, offertes maken en klantrelaties beheren.',
    cleanerDesc: 'Als Kuisman kun je je geplande reinigingen bekijken en voltooien via de agenda.',
    
    // Status
    loading: 'Laden...',
    saving: 'Opslaan...',
    saved: 'Opgeslagen',
    error: 'Fout',
    success: 'Succes',
    pending: 'In afwachting',
    completed: 'Voltooid',
    cancelled: 'Geannuleerd',
    active: 'Actief',
    inactive: 'Inactief',
    
    // Quotes & Invoices
    quoteNumber: 'Offerte Nummer',
    invoiceNumber: 'Factuur Nummer',
    status: 'Status',
    date: 'Datum',
    amount: 'Bedrag',
    customer: 'Klant',
    totalAmount: 'Totaal Bedrag',
    subtotal: 'Subtotaal',
    vat: 'BTW',
    discount: 'Korting',
    
    // Stock
    stockManagement: 'Voorraadbeheer',
    lowStock: 'Lage voorraad',
    inStock: 'Op voorraad',
    outOfStock: 'Niet op voorraad',
    quantity: 'Aantal',
    product: 'Product',
    price: 'Prijs',
    
    // Cleaning & Agenda
    startTime: 'Starttijd',
    endTime: 'Eindtijd',
    duration: 'Duur',
    today: 'Vandaag',
    week: 'Week',
    month: 'Maand',
    day: 'Dag',
    allDay: 'Hele dag',
    noEventsToday: 'Geen afspraken vandaag',
    scheduledCleanings: 'Geplande Reinigingen',
    upcomingCleanings: 'Komende Reinigingen',
    completedCleanings: 'Voltooide Reinigingen',
    cleaningDetails: 'Reinigingsdetails',
    markAsCompleted: 'Markeer als Voltooid',
    panels: 'Panelen',
    notes: 'Opmerkingen',
    addNotes: 'Opmerkingen toevoegen',
    
    // Customer & Property
    customerName: 'Klantnaam',
    propertyAddress: 'Adres',
    contactInfo: 'Contactgegevens',
    phoneNumber: 'Telefoonnummer',
    emailAddress: 'E-mailadres',
    city: 'Stad',
    postalCode: 'Postcode',
    country: 'Land',
    
    // Time
    hours: 'uur',
    minutes: 'min',
    monday: 'Maandag',
    tuesday: 'Dinsdag',
    wednesday: 'Woensdag',
    thursday: 'Donderdag',
    friday: 'Vrijdag',
    saturday: 'Zaterdag',
    sunday: 'Zondag',
    
    // Messages
    passwordChanged: 'Wachtwoord succesvol gewijzigd',
    settingsSaved: 'Instellingen opgeslagen',
    languageChanged: 'Taal gewijzigd',
    errorOccurred: 'Er is een fout opgetreden',
    confirmDelete: 'Weet je zeker dat je dit wilt verwijderen?',
    noDataFound: 'Geen gegevens gevonden',
    
    // Cleaner specific
    mySchedule: 'Mijn Planning',
    todaysTasks: 'Taken van Vandaag',
    taskDetails: 'Taakdetails',
    startCleaning: 'Start Reiniging',
    finishCleaning: 'Reiniging Afronden',
    travelTo: 'Reis naar',
    arrivedAt: 'Aangekomen om',
    startedAt: 'Gestart om',
    finishedAt: 'Afgerond om',
    numberOfPanels: 'Aantal panelen',
    beforePhoto: 'Foto voor',
    afterPhoto: 'Foto na',
    addPhoto: 'Foto toevoegen',
    submitReport: 'Rapport indienen',
    viewRoute: 'Route bekijken',
    callCustomer: 'Bel klant',
    navigate: 'Navigeren',
    
    // Additional strings for full translation coverage
    cleaningTitle: 'Reiniging',
    cleaningDescription: 'Plan en beheer zonnepaneel reiniging',
    scheduleCleaning: 'Reiniging Inplannen',
    schedule: 'Inplannen',
    noCleanigsYet: 'Nog geen reinigingen',
    noCleaningsDesc: 'Voeg een eerste reinigingsdienst toe voor een klant',
    confirmationEmail: 'Bevestigingsmail',
    noEmailSend: 'Geen mail versturen',
    sendEmailNow: 'Mail direct verzenden',
    queueEmail: 'Mail klaarzetten',
    emailWillBeSentNow: 'Mail wordt meteen verstuurd',
    emailWillBeQueued: 'Mail komt in de wachtrij',
    selectDate: 'Selecteer datum',
    time: 'Tijd',
    endDate: 'Einddatum',
    optional: 'optioneel',
    remarks: 'Opmerkingen',
    panelCleaning: 'Paneel Reiniging',
    expired: 'VERLOPEN',
    reschedule: 'Opnieuw inplannen',
    viewDetails: 'Details',
    sendConfirmation: 'Bevestiging sturen',
    deleteAppointment: 'Afspraak verwijderen',
    confirmDeleteCleaning: 'Weet je zeker dat je deze reiniging wilt verwijderen?',
    cleaningScheduled: 'Reiniging ingepland',
    cleaningDeleted: 'Reiniging verwijderd',
    couldNotLoadCleanings: 'Kon reinigingen niet laden',
    couldNotScheduleCleaning: 'Kon reiniging niet inplannen',
    couldNotDelete: 'Kon niet verwijderen',
    couldNotReschedule: 'Kon niet opnieuw inplannen',
    confirmationEmailQueued: 'Bevestigingsmail klaargezet in wachtrij',
    mailQueued: 'Mail klaargezet in wachtrij',
    
    // Agenda specific
    agendaTitle: 'Agenda',
    agendaDescription: 'Bekijk en beheer je afspraken',
    noAppointmentsToday: 'Geen afspraken vandaag',
    appointment: 'Afspraak',
    appointments: 'Afspraken',
    addAppointment: 'Afspraak toevoegen',
    editAppointment: 'Afspraak bewerken',
    appointmentDetails: 'Afspraakdetails',
    
    // Customer details
    firstName: 'Voornaam',
    lastName: 'Achternaam',
    company: 'Bedrijf',
    street: 'Straat',
    houseNumber: 'Huisnummer',
    newCustomer: 'Nieuwe klant',
    editCustomer: 'Klant bewerken',
    customerDetails: 'Klantgegevens',
    noCustomers: 'Geen klanten',
    addCustomer: 'Klant toevoegen',
    
    // Common UI elements
    actions: 'Acties',
    details: 'Details',
    information: 'Informatie',
    description: 'Beschrijving',
    name: 'Naam',
    type: 'Type',
    total: 'Totaal',
    perPage: 'per pagina',
    showingResults: 'Toont resultaten',
    of: 'van',
    all: 'Alle',
    none: 'Geen',
    yes: 'Ja',
    no: 'Nee',
    or: 'of',
    and: 'en',
    from: 'Van',
    to: 'Tot',
    at: 'om',
    on: 'op',
    by: 'door',
    
    // Form validation
    required: 'Verplicht',
    invalid: 'Ongeldig',
    tooShort: 'Te kort',
    tooLong: 'Te lang',
    
    // Notifications
    newNotification: 'Nieuwe melding',
    markAsRead: 'Markeer als gelezen',
    markAllAsRead: 'Alles als gelezen markeren',
    noNotifications: 'Geen meldingen',
    
    // Extra cleaning strings
    sameDay: 'Zelfde dag',
    startTime: 'Starttijd',
    endTime: 'Eindtijd',
    performedBy: 'Uitgevoerd door',
    technicianName: 'Naam technicus',
    extraNotesForCleaner: 'Extra opmerkingen voor de kuisman...',
    instructionPhoto: 'Instructiefoto',
    searchAddress: 'Zoek adres...',
    panels: 'panelen',
  },
  
  en: {
    // Navigation & General
    dashboard: 'Dashboard',
    customers: 'Customers',
    properties: 'Properties',
    cleaning: 'Cleaning',
    installations: 'Installations',
    quotes: 'Quotes',
    invoices: 'Invoices',
    agenda: 'Schedule',
    stock: 'Inventory',
    settings: 'Settings',
    logout: 'Logout',
    collapse: 'Collapse',
    expand: 'Expand',
    platformAdmin: 'Platform Admin',
    products: 'Products',
    requests: 'Requests',
    salesOverview: 'Sales',
    admin: 'Admin',
    map: 'Map',
    mails: 'Mails',
    tickets: 'Tickets',
    
    // Header
    welcome: 'Welcome',
    welcomeBack: 'Welcome back!',
    
    // Dashboard
    overviewOfInstallations: 'Overview of your solar installations',
    totalCustomers: 'Total Customers',
    totalAddresses: 'Total Addresses',
    totalSolarPanels: 'Total Solar Panels',
    contract2Year: '2-Year Contract',
    contract3Year: '3-Year Contract',
    cleaningNeeded: 'Cleaning Needed',
    installationsOverview: 'Installations',
    installationsPerCity: 'Installations per City',
    distributionOverLocations: 'Distribution over locations',
    
    // Actions
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    import: 'Import',
    create: 'Create',
    close: 'Close',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    view: 'View',
    download: 'Download',
    upload: 'Upload',
    refresh: 'Refresh',
    
    // Settings
    settingsTitle: 'Settings',
    settingsDescription: 'Manage your account, backups and preferences',
    profile: 'Profile',
    yourAccountDetails: 'Your account details',
    appearance: 'Appearance',
    chooseTheme: 'Choose your preferred theme',
    dark: 'Dark',
    light: 'Light',
    darkMode: 'Dark mode',
    lightMode: 'Light mode',
    language: 'Language',
    chooseLanguage: 'Choose your preferred language',
    dutch: 'Nederlands',
    english: 'English',
    polish: 'Polski',
    ukrainian: 'Українська',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    updatePassword: 'Update Password',
    roleAndPermissions: 'Role & Permissions',
    yourAccessLevel: 'Your access level in the system',
    systemInfo: 'System Information',
    platformDetails: 'Platform details',
    platform: 'Platform',
    version: 'Version',
    autoBackup: 'Auto-backup',
    every10Minutes: 'Every 10 minutes',
    companyDetails: 'Company Details',
    companyDetailsDesc: 'This information is used on quotes and documents',
    companyLogo: 'Company Logo',
    companyName: 'Company Name',
    address: 'Address',
    vatNumber: 'VAT Number',
    email: 'Email',
    phone: 'Phone',
    
    // Roles
    administrator: 'Administrator',
    technician: 'Technician',
    sales: 'Sales',
    cleaner: 'Cleaner',
    adminDesc: 'As Administrator you have full access to all features, including user management, data deletion, backups and system settings.',
    technicianDesc: 'As Technician you can manage installations, add maintenance records and schedule cleaning activities.',
    salesDesc: 'As Sales you can view customer data, create quotes and manage customer relationships.',
    cleanerDesc: 'As Cleaner you can view and complete your scheduled cleanings via the schedule.',
    
    // Status
    loading: 'Loading...',
    saving: 'Saving...',
    saved: 'Saved',
    error: 'Error',
    success: 'Success',
    pending: 'Pending',
    completed: 'Completed',
    cancelled: 'Cancelled',
    active: 'Active',
    inactive: 'Inactive',
    
    // Quotes & Invoices
    quoteNumber: 'Quote Number',
    invoiceNumber: 'Invoice Number',
    status: 'Status',
    date: 'Date',
    amount: 'Amount',
    customer: 'Customer',
    totalAmount: 'Total Amount',
    subtotal: 'Subtotal',
    vat: 'VAT',
    discount: 'Discount',
    
    // Stock
    stockManagement: 'Inventory Management',
    lowStock: 'Low stock',
    inStock: 'In stock',
    outOfStock: 'Out of stock',
    quantity: 'Quantity',
    product: 'Product',
    price: 'Price',
    
    // Cleaning & Agenda
    startTime: 'Start Time',
    endTime: 'End Time',
    duration: 'Duration',
    today: 'Today',
    week: 'Week',
    month: 'Month',
    day: 'Day',
    allDay: 'All day',
    noEventsToday: 'No appointments today',
    scheduledCleanings: 'Scheduled Cleanings',
    upcomingCleanings: 'Upcoming Cleanings',
    completedCleanings: 'Completed Cleanings',
    cleaningDetails: 'Cleaning Details',
    markAsCompleted: 'Mark as Completed',
    panels: 'Panels',
    notes: 'Notes',
    addNotes: 'Add notes',
    
    // Customer & Property
    customerName: 'Customer Name',
    propertyAddress: 'Address',
    contactInfo: 'Contact Information',
    phoneNumber: 'Phone Number',
    emailAddress: 'Email Address',
    city: 'City',
    postalCode: 'Postal Code',
    country: 'Country',
    
    // Time
    hours: 'hrs',
    minutes: 'min',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    
    // Messages
    passwordChanged: 'Password changed successfully',
    settingsSaved: 'Settings saved',
    languageChanged: 'Language changed',
    errorOccurred: 'An error occurred',
    confirmDelete: 'Are you sure you want to delete this?',
    noDataFound: 'No data found',
    
    // Cleaner specific
    mySchedule: 'My Schedule',
    todaysTasks: 'Today\'s Tasks',
    taskDetails: 'Task Details',
    startCleaning: 'Start Cleaning',
    finishCleaning: 'Finish Cleaning',
    travelTo: 'Travel to',
    arrivedAt: 'Arrived at',
    startedAt: 'Started at',
    finishedAt: 'Finished at',
    numberOfPanels: 'Number of panels',
    beforePhoto: 'Before photo',
    afterPhoto: 'After photo',
    addPhoto: 'Add photo',
    submitReport: 'Submit report',
    viewRoute: 'View route',
    callCustomer: 'Call customer',
    navigate: 'Navigate',
  },
  
  pl: {
    // Navigation & General
    dashboard: 'Panel',
    customers: 'Klienci',
    properties: 'Nieruchomości',
    cleaning: 'Czyszczenie',
    installations: 'Instalacje',
    quotes: 'Oferty',
    invoices: 'Faktury',
    agenda: 'Harmonogram',
    stock: 'Magazyn',
    settings: 'Ustawienia',
    logout: 'Wyloguj',
    collapse: 'Zwiń',
    expand: 'Rozwiń',
    platformAdmin: 'Admin Platformy',
    products: 'Produkty',
    requests: 'Zapytania',
    salesOverview: 'Sprzedaż',
    admin: 'Admin',
    map: 'Mapa',
    
    // Header
    welcome: 'Witaj',
    welcomeBack: 'Witaj ponownie!',
    
    // Dashboard
    overviewOfInstallations: 'Przegląd instalacji solarnych',
    totalCustomers: 'Łączna liczba klientów',
    totalAddresses: 'Łączna liczba adresów',
    totalSolarPanels: 'Łączna liczba paneli',
    contract2Year: 'Umowa 2-letnia',
    contract3Year: 'Umowa 3-letnia',
    cleaningNeeded: 'Wymagane czyszczenie',
    installationsOverview: 'Instalacje',
    installationsPerCity: 'Instalacje wg miast',
    distributionOverLocations: 'Rozkład według lokalizacji',
    
    // Actions
    save: 'Zapisz',
    cancel: 'Anuluj',
    delete: 'Usuń',
    edit: 'Edytuj',
    add: 'Dodaj',
    search: 'Szukaj',
    filter: 'Filtr',
    export: 'Eksport',
    import: 'Import',
    create: 'Utwórz',
    close: 'Zamknij',
    confirm: 'Potwierdź',
    back: 'Wstecz',
    next: 'Dalej',
    previous: 'Poprzedni',
    view: 'Zobacz',
    download: 'Pobierz',
    upload: 'Prześlij',
    refresh: 'Odśwież',
    
    // Settings
    settingsTitle: 'Ustawienia',
    settingsDescription: 'Zarządzaj kontem, kopiami zapasowymi i preferencjami',
    profile: 'Profil',
    yourAccountDetails: 'Dane Twojego konta',
    appearance: 'Wygląd',
    chooseTheme: 'Wybierz preferowany motyw',
    dark: 'Ciemny',
    light: 'Jasny',
    darkMode: 'Tryb ciemny',
    lightMode: 'Tryb jasny',
    language: 'Język',
    chooseLanguage: 'Wybierz preferowany język',
    dutch: 'Nederlands',
    english: 'English',
    polish: 'Polski',
    ukrainian: 'Українська',
    changePassword: 'Zmień hasło',
    currentPassword: 'Obecne hasło',
    newPassword: 'Nowe hasło',
    confirmPassword: 'Potwierdź hasło',
    updatePassword: 'Zaktualizuj hasło',
    roleAndPermissions: 'Rola i uprawnienia',
    yourAccessLevel: 'Twój poziom dostępu w systemie',
    systemInfo: 'Informacje o systemie',
    platformDetails: 'Szczegóły platformy',
    platform: 'Platforma',
    version: 'Wersja',
    autoBackup: 'Auto-backup',
    every10Minutes: 'Co 10 minut',
    companyDetails: 'Dane firmy',
    companyDetailsDesc: 'Te dane są używane na ofertach i dokumentach',
    companyLogo: 'Logo firmy',
    companyName: 'Nazwa firmy',
    address: 'Adres',
    vatNumber: 'Numer VAT',
    email: 'E-mail',
    phone: 'Telefon',
    
    // Roles
    administrator: 'Administrator',
    technician: 'Technik',
    sales: 'Sprzedaż',
    cleaner: 'Pracownik czyszczący',
    adminDesc: 'Jako Administrator masz pełny dostęp do wszystkich funkcji, w tym zarządzania użytkownikami, usuwania danych, kopii zapasowych i ustawień systemu.',
    technicianDesc: 'Jako Technik możesz zarządzać instalacjami, dodawać wpisy konserwacyjne i planować czyszczenie.',
    salesDesc: 'Jako Sprzedawca możesz przeglądać dane klientów, tworzyć oferty i zarządzać relacjami z klientami.',
    cleanerDesc: 'Jako Pracownik czyszczący możesz przeglądać i realizować zaplanowane czyszczenia przez harmonogram.',
    
    // Status
    loading: 'Ładowanie...',
    saving: 'Zapisywanie...',
    saved: 'Zapisano',
    error: 'Błąd',
    success: 'Sukces',
    pending: 'Oczekujące',
    completed: 'Zakończone',
    cancelled: 'Anulowane',
    active: 'Aktywny',
    inactive: 'Nieaktywny',
    
    // Quotes & Invoices
    quoteNumber: 'Numer oferty',
    invoiceNumber: 'Numer faktury',
    status: 'Status',
    date: 'Data',
    amount: 'Kwota',
    customer: 'Klient',
    totalAmount: 'Łączna kwota',
    subtotal: 'Suma częściowa',
    vat: 'VAT',
    discount: 'Rabat',
    
    // Stock
    stockManagement: 'Zarządzanie magazynem',
    lowStock: 'Niski stan',
    inStock: 'Na stanie',
    outOfStock: 'Brak na stanie',
    quantity: 'Ilość',
    product: 'Produkt',
    price: 'Cena',
    
    // Cleaning & Agenda
    startTime: 'Czas rozpoczęcia',
    endTime: 'Czas zakończenia',
    duration: 'Czas trwania',
    today: 'Dzisiaj',
    week: 'Tydzień',
    month: 'Miesiąc',
    day: 'Dzień',
    allDay: 'Cały dzień',
    noEventsToday: 'Brak spotkań na dziś',
    scheduledCleanings: 'Zaplanowane czyszczenia',
    upcomingCleanings: 'Nadchodzące czyszczenia',
    completedCleanings: 'Zakończone czyszczenia',
    cleaningDetails: 'Szczegóły czyszczenia',
    markAsCompleted: 'Oznacz jako zakończone',
    panels: 'Panele',
    notes: 'Notatki',
    addNotes: 'Dodaj notatki',
    
    // Customer & Property
    customerName: 'Nazwa klienta',
    propertyAddress: 'Adres',
    contactInfo: 'Dane kontaktowe',
    phoneNumber: 'Numer telefonu',
    emailAddress: 'Adres e-mail',
    city: 'Miasto',
    postalCode: 'Kod pocztowy',
    country: 'Kraj',
    
    // Time
    hours: 'godz',
    minutes: 'min',
    monday: 'Poniedziałek',
    tuesday: 'Wtorek',
    wednesday: 'Środa',
    thursday: 'Czwartek',
    friday: 'Piątek',
    saturday: 'Sobota',
    sunday: 'Niedziela',
    
    // Messages
    passwordChanged: 'Hasło zostało zmienione',
    settingsSaved: 'Ustawienia zapisane',
    languageChanged: 'Język zmieniony',
    errorOccurred: 'Wystąpił błąd',
    confirmDelete: 'Czy na pewno chcesz to usunąć?',
    noDataFound: 'Nie znaleziono danych',
    
    // Cleaner specific
    mySchedule: 'Mój harmonogram',
    todaysTasks: 'Zadania na dziś',
    taskDetails: 'Szczegóły zadania',
    startCleaning: 'Rozpocznij czyszczenie',
    finishCleaning: 'Zakończ czyszczenie',
    travelTo: 'Jedź do',
    arrivedAt: 'Przyjechano o',
    startedAt: 'Rozpoczęto o',
    finishedAt: 'Zakończono o',
    numberOfPanels: 'Liczba paneli',
    beforePhoto: 'Zdjęcie przed',
    afterPhoto: 'Zdjęcie po',
    addPhoto: 'Dodaj zdjęcie',
    submitReport: 'Wyślij raport',
    viewRoute: 'Zobacz trasę',
    callCustomer: 'Zadzwoń do klienta',
    navigate: 'Nawiguj',
  },
  
  uk: {
    // Navigation & General
    dashboard: 'Панель',
    customers: 'Клієнти',
    properties: 'Об\'єкти',
    cleaning: 'Прибирання',
    installations: 'Установки',
    quotes: 'Пропозиції',
    invoices: 'Рахунки',
    agenda: 'Розклад',
    stock: 'Склад',
    settings: 'Налаштування',
    logout: 'Вийти',
    collapse: 'Згорнути',
    expand: 'Розгорнути',
    platformAdmin: 'Адмін платформи',
    products: 'Продукти',
    requests: 'Запити',
    salesOverview: 'Продажі',
    admin: 'Адмін',
    map: 'Карта',
    mails: 'Пошта',
    
    // Header
    welcome: 'Ласкаво просимо',
    welcomeBack: 'З поверненням!',
    
    // Dashboard
    overviewOfInstallations: 'Огляд сонячних установок',
    totalCustomers: 'Всього клієнтів',
    totalAddresses: 'Всього адрес',
    totalSolarPanels: 'Всього панелей',
    contract2Year: '2-річний контракт',
    contract3Year: '3-річний контракт',
    cleaningNeeded: 'Потрібне прибирання',
    installationsOverview: 'Установки',
    installationsPerCity: 'Установки по містах',
    distributionOverLocations: 'Розподіл за локаціями',
    
    // Actions
    save: 'Зберегти',
    cancel: 'Скасувати',
    delete: 'Видалити',
    edit: 'Редагувати',
    add: 'Додати',
    search: 'Пошук',
    filter: 'Фільтр',
    export: 'Експорт',
    import: 'Імпорт',
    create: 'Створити',
    close: 'Закрити',
    confirm: 'Підтвердити',
    back: 'Назад',
    next: 'Далі',
    previous: 'Попередній',
    view: 'Переглянути',
    download: 'Завантажити',
    upload: 'Вивантажити',
    refresh: 'Оновити',
    
    // Settings
    settingsTitle: 'Налаштування',
    settingsDescription: 'Керуйте обліковим записом, резервними копіями та налаштуваннями',
    profile: 'Профіль',
    yourAccountDetails: 'Дані вашого облікового запису',
    appearance: 'Вигляд',
    chooseTheme: 'Оберіть бажану тему',
    dark: 'Темна',
    light: 'Світла',
    darkMode: 'Темний режим',
    lightMode: 'Світлий режим',
    language: 'Мова',
    chooseLanguage: 'Оберіть бажану мову',
    dutch: 'Nederlands',
    english: 'English',
    polish: 'Polski',
    ukrainian: 'Українська',
    changePassword: 'Змінити пароль',
    currentPassword: 'Поточний пароль',
    newPassword: 'Новий пароль',
    confirmPassword: 'Підтвердити пароль',
    updatePassword: 'Оновити пароль',
    roleAndPermissions: 'Роль та дозволи',
    yourAccessLevel: 'Ваш рівень доступу в системі',
    systemInfo: 'Системна інформація',
    platformDetails: 'Деталі платформи',
    platform: 'Платформа',
    version: 'Версія',
    autoBackup: 'Авто-резервування',
    every10Minutes: 'Кожні 10 хвилин',
    companyDetails: 'Дані компанії',
    companyDetailsDesc: 'Ці дані використовуються в пропозиціях та документах',
    companyLogo: 'Логотип компанії',
    companyName: 'Назва компанії',
    address: 'Адреса',
    vatNumber: 'Номер ПДВ',
    email: 'Ел. пошта',
    phone: 'Телефон',
    
    // Roles
    administrator: 'Адміністратор',
    technician: 'Технік',
    sales: 'Продажі',
    cleaner: 'Прибиральник',
    adminDesc: 'Як Адміністратор ви маєте повний доступ до всіх функцій, включаючи управління користувачами, видалення даних, резервне копіювання та системні налаштування.',
    technicianDesc: 'Як Технік ви можете керувати установками, додавати записи технічного обслуговування та планувати прибирання.',
    salesDesc: 'Як Продавець ви можете переглядати дані клієнтів, створювати пропозиції та керувати відносинами з клієнтами.',
    cleanerDesc: 'Як Прибиральник ви можете переглядати та виконувати заплановані прибирання через розклад.',
    
    // Status
    loading: 'Завантаження...',
    saving: 'Збереження...',
    saved: 'Збережено',
    error: 'Помилка',
    success: 'Успіх',
    pending: 'Очікує',
    completed: 'Завершено',
    cancelled: 'Скасовано',
    active: 'Активний',
    inactive: 'Неактивний',
    
    // Quotes & Invoices
    quoteNumber: 'Номер пропозиції',
    invoiceNumber: 'Номер рахунку',
    status: 'Статус',
    date: 'Дата',
    amount: 'Сума',
    customer: 'Клієнт',
    totalAmount: 'Загальна сума',
    subtotal: 'Проміжний підсумок',
    vat: 'ПДВ',
    discount: 'Знижка',
    
    // Stock
    stockManagement: 'Управління складом',
    lowStock: 'Низький запас',
    inStock: 'В наявності',
    outOfStock: 'Немає в наявності',
    quantity: 'Кількість',
    product: 'Продукт',
    price: 'Ціна',
    
    // Cleaning & Agenda
    startTime: 'Час початку',
    endTime: 'Час закінчення',
    duration: 'Тривалість',
    today: 'Сьогодні',
    week: 'Тиждень',
    month: 'Місяць',
    day: 'День',
    allDay: 'Весь день',
    noEventsToday: 'Немає подій на сьогодні',
    scheduledCleanings: 'Заплановані прибирання',
    upcomingCleanings: 'Майбутні прибирання',
    completedCleanings: 'Завершені прибирання',
    cleaningDetails: 'Деталі прибирання',
    markAsCompleted: 'Позначити як завершене',
    panels: 'Панелі',
    notes: 'Нотатки',
    addNotes: 'Додати нотатки',
    
    // Customer & Property
    customerName: 'Ім\'я клієнта',
    propertyAddress: 'Адреса',
    contactInfo: 'Контактна інформація',
    phoneNumber: 'Номер телефону',
    emailAddress: 'Електронна адреса',
    city: 'Місто',
    postalCode: 'Поштовий індекс',
    country: 'Країна',
    
    // Time
    hours: 'год',
    minutes: 'хв',
    monday: 'Понеділок',
    tuesday: 'Вівторок',
    wednesday: 'Середа',
    thursday: 'Четвер',
    friday: 'П\'ятниця',
    saturday: 'Субота',
    sunday: 'Неділя',
    
    // Messages
    passwordChanged: 'Пароль успішно змінено',
    settingsSaved: 'Налаштування збережено',
    languageChanged: 'Мову змінено',
    errorOccurred: 'Сталася помилка',
    confirmDelete: 'Ви впевнені, що хочете видалити це?',
    noDataFound: 'Даних не знайдено',
    
    // Cleaner specific
    mySchedule: 'Мій розклад',
    todaysTasks: 'Завдання на сьогодні',
    taskDetails: 'Деталі завдання',
    startCleaning: 'Почати прибирання',
    finishCleaning: 'Завершити прибирання',
    travelTo: 'Їхати до',
    arrivedAt: 'Прибув о',
    startedAt: 'Почато о',
    finishedAt: 'Завершено о',
    numberOfPanels: 'Кількість панелей',
    beforePhoto: 'Фото до',
    afterPhoto: 'Фото після',
    addPhoto: 'Додати фото',
    submitReport: 'Надіслати звіт',
    viewRoute: 'Переглянути маршрут',
    callCustomer: 'Зателефонувати клієнту',
    navigate: 'Навігація',
    
    // Additional strings for full translation coverage
    tickets: 'Заявки',
    cleaningTitle: 'Прибирання',
    cleaningDescription: 'Плануйте та керуйте прибиранням сонячних панелей',
    scheduleCleaning: 'Запланувати прибирання',
    schedule: 'Запланувати',
    noCleanigsYet: 'Ще немає прибирань',
    noCleaningsDesc: 'Додайте першу послугу з прибирання для клієнта',
    confirmationEmail: 'Лист-підтвердження',
    noEmailSend: 'Не надсилати листа',
    sendEmailNow: 'Надіслати листа зараз',
    queueEmail: 'Поставити лист у чергу',
    emailWillBeSentNow: 'Лист буде надіслано негайно',
    emailWillBeQueued: 'Лист буде поставлено в чергу',
    selectDate: 'Виберіть дату',
    time: 'Час',
    endDate: 'Кінцева дата',
    optional: 'необов\'язково',
    remarks: 'Зауваження',
    panelCleaning: 'Прибирання панелей',
    expired: 'ПРОСТРОЧЕНО',
    reschedule: 'Перепланувати',
    viewDetails: 'Деталі',
    sendConfirmation: 'Надіслати підтвердження',
    deleteAppointment: 'Видалити зустріч',
    confirmDeleteCleaning: 'Ви впевнені, що хочете видалити це прибирання?',
    cleaningScheduled: 'Прибирання заплановано',
    cleaningDeleted: 'Прибирання видалено',
    couldNotLoadCleanings: 'Не вдалося завантажити прибирання',
    couldNotScheduleCleaning: 'Не вдалося запланувати прибирання',
    couldNotDelete: 'Не вдалося видалити',
    couldNotReschedule: 'Не вдалося перепланувати',
    confirmationEmailQueued: 'Лист-підтвердження поставлено в чергу',
    mailQueued: 'Лист поставлено в чергу',
    
    // Agenda specific
    agendaTitle: 'Розклад',
    agendaDescription: 'Переглядайте та керуйте своїми зустрічами',
    noAppointmentsToday: 'Немає зустрічей на сьогодні',
    appointment: 'Зустріч',
    appointments: 'Зустрічі',
    addAppointment: 'Додати зустріч',
    editAppointment: 'Редагувати зустріч',
    appointmentDetails: 'Деталі зустрічі',
    
    // Customer details
    firstName: 'Ім\'я',
    lastName: 'Прізвище',
    company: 'Компанія',
    street: 'Вулиця',
    houseNumber: 'Номер будинку',
    newCustomer: 'Новий клієнт',
    editCustomer: 'Редагувати клієнта',
    customerDetails: 'Дані клієнта',
    noCustomers: 'Немає клієнтів',
    addCustomer: 'Додати клієнта',
    
    // Common UI elements
    actions: 'Дії',
    details: 'Деталі',
    information: 'Інформація',
    description: 'Опис',
    name: 'Назва',
    type: 'Тип',
    total: 'Всього',
    perPage: 'на сторінці',
    showingResults: 'Показано результатів',
    of: 'з',
    all: 'Всі',
    none: 'Немає',
    yes: 'Так',
    no: 'Ні',
    or: 'або',
    and: 'та',
    from: 'Від',
    to: 'До',
    at: 'о',
    on: 'на',
    by: 'від',
    
    // Form validation
    required: 'Обов\'язково',
    invalid: 'Недійсний',
    tooShort: 'Занадто короткий',
    tooLong: 'Занадто довгий',
    
    // Notifications
    newNotification: 'Нове сповіщення',
    markAsRead: 'Позначити як прочитане',
    markAllAsRead: 'Позначити всі як прочитані',
    noNotifications: 'Немає сповіщень',
    
    // Extra cleaning strings
    sameDay: 'Той самий день',
    startTime: 'Час початку',
    endTime: 'Час закінчення',
    performedBy: 'Виконує',
    technicianName: 'Ім\'я техніка',
    extraNotesForCleaner: 'Додаткові примітки для прибиральника...',
    instructionPhoto: 'Фото інструкції',
    searchAddress: 'Пошук адреси...',
    panels: 'панелей',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedLanguage = localStorage.getItem('language');
      if (savedLanguage) return savedLanguage;
    }
    return 'nl'; // Default to Dutch
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Translation function
  const t = (key) => {
    return translations[language]?.[key] || translations['nl']?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export { translations };
